<template>
  <div class="quick-reply-visual-panel">
    <div class="qr-title">快捷回复</div>

    <el-input v-model="keywordSearch" class="qr-search-keywords" placeholder="搜索关键词..." clearable>
      <i slot="prefix" class="el-input__icon el-icon-search"></i>
    </el-input>

    <div class="qr-tabs">
      <button v-for="tab in tabs" :key="tab.key" type="button" class="qr-tab"
        :class="{ 'is-active': activeTab === tab.key }" @click="activeTab = tab.key">
        {{ tab.label }}
      </button>
    </div>
    <div class="qr-tab-line" />

    <!-- 问候语：系统常用语 + 我的常用语 -->
    <div v-show="activeTab === 'greeting'" class="qr-pane">
      <button type="button" class="qr-add-btn" @click="openDialog(null)">+ 添加常用语</button>
      <template v-if="(loadingSys || loadingMy) && greetingPhraseList.length === 0">
        <div class="qr-pane-state">加载中...</div>
      </template>
      <template v-else-if="filteredGreetings.length === 0">
        <div class="qr-pane-state">暂无常用语</div>
      </template>
      <template v-else>
        <div v-for="(phrase, index) in filteredGreetings" :key="phraseKey(phrase, index)" class="qr-msg-card">
          <div class="qr-msg-content" @click="onPhraseClick(phrase)">
            {{ phraseBody(phrase) }}
          </div>
          <div class="qr-msg-actions">
            <img class="qr-del-btn" src="../../assets/svg/edit.svg" @click.stop="openEdit(phrase)" alt="编辑" />
            <img class="qr-del-btn" src="../../assets/svg/delete.svg" @click.stop="openDelete(phrase)" alt="删除" />
          </div>
        </div>
      </template>
    </div>

    <!-- 转接 -->
    <div v-show="activeTab === 'transfer'" class="qr-pane qr-pane-transfer">
      <div class="qr-section-label">查找客服</div>
      <el-input v-model="agentSearch" class="qr-search-agent" placeholder="搜索姓名或工号..." clearable>
        <i slot="prefix" class="el-input__icon el-icon-search"></i>
      </el-input>

      <div class="qr-rec-row">
        <span class="qr-rec-title">推荐客服 (在线)</span>
        <a v-if="showTransferViewAllLink" href="#" class="qr-link-all"
          @click.prevent="showAllTransferAgents = true">查看全部</a>
        <a v-else-if="showTransferCollapseLink" href="#" class="qr-link-all"
          @click.prevent="showAllTransferAgents = false">收起</a>
      </div>

      <div class="qr-transfer-list-host">
        <cus-fixed-list class="qr-transfer-scroller" :items="displayTransferAgents" :item-size="60" key-field="id"
          :loading="onLineListLoading" empty-text="暂无在线客服">
          <template #default="{ item }">
            <div class="list-item-wrap">
              <div class="item-inner">
                <div class="qr-agent-card qr-agent-card--vlist"
                  :class="{ 'is-selected': isTransferAgentSelected(item) }"
                  @click.stop="onTransferAgentCardClick(item)">
                  <div class="qr-agent-left">
                    <div class="qr-agent-avatar-wrap qr-agent-avatar-wrap--vlist">
                      <head-image :id="item.userId || item.id" :name="displayNickName(item)"
                        :url="item.headImage || item.headImageThumb" :size="36" radius="10px" :online="item.online"
                        :isShowUserInfo="false" />
                    </div>
                    <span class="qr-agent-name">{{ displayNickName(item) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </cus-fixed-list>
      </div>

      <div class="qr-section-label">转接备注</div>
      <el-input v-model="transferRemark" type="textarea" :rows="4" resize="none" class="qr-remark-input"
        placeholder="请输入给接收客服的备注信息，如问题摘要或特殊要求..." />
      <div v-if="selectedTransferAgent" class="qr-transfer-actions">
        <el-button type="primary" class="qr-transfer-confirm-btn" :loading="transferApplying" @click="applyTransfer">
          确认转接
        </el-button>
      </div>
    </div>

    <!-- 其他 -->
    <div v-show="activeTab === 'other'" class="qr-pane">
      <div class="qr-pane-state">暂无内容</div>
    </div>

    <el-dialog :title="editingPhrase ? '编辑常用语' : '新增常用语'" :visible.sync="dialogVisible" width="420px" append-to-body
      @closed="onDialogClosed">
      <el-input v-model="phraseContent" type="textarea" :rows="5" placeholder="请输入常用语内容" maxlength="2000"
        show-word-limit />
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="savingPhrase" :disabled="!phraseContent.trim()" @click="savePhrase">
          {{ savingPhrase ? "保存中..." : "确 定" }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import HeadImage from "../common/HeadImage.vue";
import CusFixedList from "./cus-recycle-scroller.vue";

export default {
  name: "QuickReplyVisualPanel",
  components: {
    HeadImage,
    CusFixedList
  },
  props: {
    // 当前左侧选中的会话，用于转接时传访客 userId
    chat: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      activeTab: "greeting", // 默认选中的tab
      tabs: [
        { key: "greeting", label: "问候语" },
        { key: "transfer", label: "转接" },
        { key: "other", label: "其他" }
      ], // 所有tab
      keywordSearch: "", // 搜索关键词
      agentSearch: "", // 搜索客服
      transferRemark: "", // 转接备注
      systemCommonPhrases: [], // 系统常用语
      myCommonPhrases: [], // 我的常用语
      loadingSys: false, // 系统常用语加载中
      loadingMy: false, // 我的常用语加载中
      onLineList: [], // 在线客服列表
      onLineListLoading: false, // 在线客服列表加载中
      dialogVisible: false, // 编辑常用语弹窗
      editingPhrase: null, // 编辑常用语
      phraseContent: "", // 编辑常用语内容
      savingPhrase: false, // 编辑常用语保存中
      showAllTransferAgents: false, // 是否展开全部客服
      selectedTransferAgent: null, // 选中的客服
      transferApplying: false // 转接中
    };
  },
  computed: {
    //访客侧 userId
    visitorTransferUserId() {
      return this.chat && this.chat.targetId != null ? this.chat.targetId : null;
    },
    // 所有常用于  系统+我的
    greetingPhraseList() {
      const sys = this.systemCommonPhrases || [];
      const my = this.myCommonPhrases || [];
      return [...sys, ...my];
    },
    // 问候语列表
    filteredGreetings() {
      const list = this.greetingPhraseList;
      const q = (this.keywordSearch || "").trim().toLowerCase();
      if (!q) {
        return list;
      }
      return list.filter((p) => {
        const body = this.phraseBody(p).toLowerCase();
        const title = this.displayTitle(p).toLowerCase();
        return body.includes(q) || title.includes(q);
      });
    },
    // 在线客服列表
    filteredTransferAgentsFull() {
      const list = Array.isArray(this.onLineList) ? this.onLineList : [];
      const kw = (this.agentSearch || "").trim().toLowerCase();
      if (!kw) {
        return list;
      }
      return list.filter((item) => {
        const name = String(item.nickName || "").toLowerCase();
        const work = String(item.workNo ?? item.jobNo ?? item.workNum ?? item.userName ?? "").toLowerCase();
        return name.includes(kw) || work.includes(kw);
      });
    },
    /** 无搜索且未展开时只展示前 3 条；有搜索时展示全部匹配结果 */
    displayTransferAgents() {
      const full = this.filteredTransferAgentsFull;
      const kw = (this.agentSearch || "").trim();
      if (kw) return full;
      if (this.showAllTransferAgents) return full;
      return full.slice(0, 3);
    },
    // 是否显示查看全部客服链接
    showTransferViewAllLink() {
      const kw = (this.agentSearch || "").trim();
      if (kw) return false;
      return this.filteredTransferAgentsFull.length > 3 && !this.showAllTransferAgents;
    },
    // 是否显示收起客服链接
    showTransferCollapseLink() {
      const kw = (this.agentSearch || "").trim();
      if (kw) return false;
      return this.showAllTransferAgents && this.filteredTransferAgentsFull.length > 3;
    }
  },
  watch: {
    // 当前左侧选中的会话，用于转接时传访客 userId
    chat() {
      this.selectedTransferAgent = null;
    },
    // tab切换
    activeTab(val) {
      if (val !== "transfer") {
        this.showAllTransferAgents = false;
        this.selectedTransferAgent = null;
      }
      if (val === "transfer") {
        this.getOnlineList();
      }
      if (val === "greeting" && this.systemCommonPhrases.length === 0 && !this.loadingSys) {
        this.fetchSystemCommonPhrases();
      }
      if (val === "greeting" && this.myCommonPhrases.length === 0 && !this.loadingMy) {
        this.fetchMyCommonPhrases();
      }
    }
  },
  mounted() {
    this.fetchMyCommonPhrases();
    this.fetchSystemCommonPhrases();
  },
  methods: {
    phraseBody(phrase) {
      if (!phrase) return "";
      return String(phrase.content != null ? phrase.content : phrase.expression || "").trim();
    },
    displayTitle(phrase) {
      if (!phrase) return "常用语";
      const raw = phrase.title || phrase.name || phrase.keyword || phrase.label || phrase.shortTitle || "";
      if (raw != null && String(raw).trim() !== "") {
        return String(raw).trim();
      }
      const c = this.phraseBody(phrase);
      if (!c) return "常用语";
      return c.length > 16 ? `${c.slice(0, 16)}…` : c;
    },
    phraseKey(phrase, index) {
      return phrase.id != null ? `p-${phrase.id}` : `i-${index}`;
    },
    // 添加常用语
    onPhraseClick(phrase) {
      const text = this.phraseBody(phrase);
      if (!text) return;
      this.$emit("apply-text", text);
    },

    // 编辑常用语
    openEdit(phrase) {
      if (!phrase || !phrase.id) {
        this.$message.warning("无法编辑，缺少ID信息");
        return;
      }
      this.editingPhrase = phrase; // 记录正在编辑的常用语对象
      this.phraseContent = this.phraseBody(phrase); // 填充内容到输入框
      this.dialogVisible = true;
    },

    // 删除常用语
    openDelete(phrase) {
      if (!phrase || !phrase.id) {
        this.$message.warning("无法删除，缺少ID信息");
        return;
      }
      this.$confirm("确定要删除这条常用语吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      })
        .then(() => {
          this.$http({
            url: "/commonExpressions/delById",
            method: "get",
            params: {
              id: phrase.id
            }
          })
            .then(() => {
              this.$message.success("删除成功");
              // 删除成功后刷新“我的常用语”列表
              this.fetchMyCommonPhrases();
            })
            .catch(() => {
              this.$message.error("删除失败，请重试");
            });
        })
        .catch(() => {
        });
    },

    displayNickName(agent) {
      return agent && agent.nickName ? agent.nickName : agent.userName;
    },
    // agentLoadDisplay(agent) {
    //   if (!agent) return "";
    //   const cur = agent.curSession ?? agent.currentSessions ?? agent.busyNum ?? agent.acceptNum ?? agent.currentCount;
    //   const max = agent.maxSession ?? agent.maxSessions ?? agent.limit ?? agent.maxCount ?? agent.capacityNum;
    //   if (cur != null && cur !== "" && max != null && max !== "") {
    //     return `${cur}/${max}`;
    //   }
    //   if (agent.loadText || agent.capacityText) {
    //     return String(agent.loadText || agent.capacityText);
    //   }
    //   return "";
    // },
    // agentLoadFull(agent) {
    //   const cur = agent.curSession ?? agent.currentSessions ?? agent.busyNum ?? agent.acceptNum ?? agent.currentCount;
    //   const max = agent.maxSession ?? agent.maxSessions ?? agent.limit ?? agent.maxCount ?? agent.capacityNum;
    //   if (max != null && max !== "" && Number(max) > 0) {
    //     return Number(cur) >= Number(max);
    //   }
    //   return !!agent.full;
    // },
    isTransferAgentSelected(agent) {
      if (!this.selectedTransferAgent || !agent) return false;
      const sa = this.selectedTransferAgent.userId ?? this.selectedTransferAgent.id;
      const sb = agent.userId ?? agent.id;
      return sa != null && sb != null && String(sa) === String(sb);
    },
    onTransferAgentCardClick(agent) {
      if (this.isTransferAgentSelected(agent)) {
        this.selectedTransferAgent = null;
        this.$emit("select-agent", null);
        return;
      }
      this.selectedTransferAgent = agent;
      this.$emit("select-agent", agent);
    },
    // 转接
    applyTransfer() {
      const reason = (this.transferRemark || "").trim();
      // if (!reason) {
      //   this.$message.warning("请输入转接备注");
      //   return;
      // }
      if (!this.selectedTransferAgent) {
        this.$message.warning("请选择接收客服");
        return;
      }
      const vid = this.visitorTransferUserId;
      if (vid == null || vid === "") {
        this.$message.warning("请先选择左侧会话");
        return;
      }
      const toUserId = this.selectedTransferAgent.userId ?? this.selectedTransferAgent.id;
      if (toUserId == null || toUserId === "") {
        this.$message.warning("目标客服信息不完整");
        return;
      }
      this.transferApplying = true;
      this.$http({
        url: "/customer/transfer/apply",
        method: "POST",
        data: {
          userId: vid,
          transferReason: reason,
          toUserId
        }
      })
        .then(() => {
          this.$message.success("转接成功");
          this.$emit("transfer-success", {
            agent: this.selectedTransferAgent,
            chat: this.chat,
            transferReason: reason
          });
          this.transferRemark = "";
          this.selectedTransferAgent = null;
        })
        .catch(() => {
          this.$message.error("转接失败，请重试");
        })
        .finally(() => {
          this.transferApplying = false;
        });
    },
    // 问候语是 系统常用 + 我的常用语
    // 获取系统常用语
    fetchSystemCommonPhrases() {
      this.loadingSys = true;
      this.$http({
        url: "/commonExpressions/getSysExpressionsList",
        method: "get"
      })
        .then((response) => {
          this.systemCommonPhrases = Array.isArray(response) ? response : [];
        })
        .catch(() => {
          this.systemCommonPhrases = [];
        })
        .finally(() => {
          this.loadingSys = false;
        });
    },
    // 获取我的常用语
    fetchMyCommonPhrases() {
      this.loadingMy = true;
      this.$http({
        url: "/commonExpressions/getCommonExpressionsList",
        method: "get"
      })
        .then((response) => {
          this.myCommonPhrases = Array.isArray(response) ? response : [];
        })
        .catch(() => {
          this.myCommonPhrases = [];
        })
        .finally(() => {
          this.loadingMy = false;
        });
    },
    // 获取在线客服列表  也就是转接列表
    getOnlineList() {
      this.onLineListLoading = true;
      this.$http({
        url: "/customer/transfer/online",
        method: "POST"
      })
        .then((res) => {
          this.onLineList = Array.isArray(res) ? res : [];
          this.selectedTransferAgent = null;
        })
        .catch(() => {
          this.onLineList = [];
          this.selectedTransferAgent = null;
        })
        .finally(() => {
          this.onLineListLoading = false;
        });
    },
    openDialog(phrase) {
      if (phrase) {
        this.editingPhrase = phrase;
        this.phraseContent = this.phraseBody(phrase);
      } else {
        this.editingPhrase = null;
        this.phraseContent = "";
      }
      this.dialogVisible = true;
    },
    onDialogClosed() {
      this.editingPhrase = null;
      this.phraseContent = "";
    },
    savePhrase() {
      const t = this.phraseContent.trim();
      if (!t) {
        this.$message.warning("常用语内容不能为空");
        return;
      }
      this.savingPhrase = true;
      const data = {
        id: this.editingPhrase ? this.editingPhrase.id : null,
        content: t
      };
      this.$http({
        url: "/commonExpressions/addOrEdit",
        method: "post",
        data
      })
        .then((res) => {
          this.$message.success(this.editingPhrase ? "修改成功" : "新增成功");
          this.dialogVisible = false;
          this.fetchMyCommonPhrases();
        })
        .catch(() => { })
        .finally(() => {
          this.savingPhrase = false;
        });
    }
  }
};
</script>

<style scoped lang="scss">
.quick-reply-visual-panel {
  box-sizing: border-box;
  width: 100%;
  padding: 12px 16px 20px;
  background: #fff;
  font-family: "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif;
  color: #303133;
}

.qr-title {
  margin-bottom: 12px;
  font-size: 12px;
  line-height: 18px;
  color: #909399;
  font-weight: 400;
  text-align: left;
}

/* 快捷回复顶部搜索：浅灰底、圆角 */
::v-deep .qr-search-keywords.el-input .el-input__inner {
  height: 33px;
  line-height: 33px;
  padding-left: 36px;
  font-size: 12px;
  color: #303133;
  border-radius: 4px;
  // border: 1px solid #f3f4f6;
  background: #f9fafb;

  &::placeholder {
    color: #6b7280;
  }
}

::v-deep .qr-search-keywords .el-input__prefix {
  left: 12px;
  font-size: 12px;
}

::v-deep .qr-search-keywords .el-input__icon {
  color: #909399;
  line-height: 34px !important;
}

.qr-tabs {
  display: flex;
  align-items: flex-end;
  gap: 24px;
  margin-top: 16px;
  padding-bottom: 0;
}

.qr-tab {
  position: relative;
  padding: 0 2px 10px;
  border: none;
  background: transparent;
  font-size: 12px;
  line-height: 22px;
  color: #6b7280;
  cursor: pointer;
  outline: none;

  &.is-active {
    color: #2563eb;
    font-weight: 400;

    &::after {
      content: "";
      position: absolute;
      left: 50%;
      bottom: 0;
      transform: translateX(-50%);
      width: 28px;
      height: 3px;
      border-radius: 2px;
      background: #2563eb;
    }
  }

  &:hover:not(.is-active) {
    color: #303133;
  }
}

.qr-tab-line {
  height: 1px;
  margin-top: -1px;
  margin-bottom: 14px;
  background: #e4e7ed;
}

.qr-pane {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.qr-pane-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 64px;
  padding: 12px;
  font-size: 13px;
  color: #909399;
}

.qr-msg-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: 16px;
  background: #f9fafb;
  font-size: 13px;
  line-height: 1.65;
  color: #1f2937;
  cursor: pointer;
  gap: 10px;
  transition: background 0.15s ease;

  &:hover {
    background: #eef0f4;
  }
}

.qr-msg-content {
  flex: 1;
  text-align: left;
}

.qr-msg-actions {
  display: flex;
  gap: 8px;
  margin-left: 8px;
}

.qr-del-btn {
  width: 15px;
  height: 15px;
}

.qr-add-btn {
  align-self: flex-start;
  margin-top: 4px;
  padding: 12px;
  border: none;
  background: #f7f8fa;
  font-size: 12px;
  line-height: 20px;
  color: #1f2937;
  cursor: pointer;
  border-radius: 16px;
  margin: 4px auto 0 auto;

  &:hover {
    background: #eef0f4;
    color: #303133;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

/* —— 转接 —— */
.qr-pane-transfer {
  gap: 0;
}

.qr-section-label {
  margin-bottom: 8px;
  font-size: 12px;
  line-height: 20px;
  font-weight: 400;
  color: #434654;
  text-align: left;
}

.qr-pane-transfer .qr-section-label:not(:first-child) {
  margin-top: 18px;
}

::v-deep .qr-search-agent.el-input .el-input__inner {
  height: 34px;
  line-height: 34px;
  padding-left: 34px;
  font-size: 13px;
  color: #303133;
  border-radius: 12px;
  // border: 1px solid #c3c6d7;
  background: #fff;

  &::placeholder {
    color: #6b7280;
  }
}

::v-deep .qr-search-agent .el-input__prefix {
  left: 12px;
}

::v-deep .qr-search-agent .el-input__icon {
  color: #737686;
  line-height: 34px !important;
}

.qr-rec-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  margin-bottom: 10px;
}

.qr-rec-title {
  color: #434654;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  /* 200% */
}

.qr-link-all {
  text-decoration: none;
  cursor: pointer;
  color: #003da6;
  font-size: 8px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;

  &:hover {
    opacity: 0.85;
  }
}

.qr-transfer-list-host {
  height: 230px;
  min-height: 100px;
  max-height: min(230px, 38vh);
}

.qr-transfer-scroller {
  height: 100%;
}

.qr-transfer-scroller ::v-deep .list-item-wrap {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: 60px;
  width: 100%;
}

.item-inner {
  width: 100%;
  height: calc(100% - 8px);
  /* 控制间隙总量 */
  margin: 4px 0;
  /* 上下各 4px 间距 */
}

.qr-agent-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

/* 虚拟列表内：行高固定 60px，与 item-size 一致 */
.qr-transfer-scroller .qr-agent-card.qr-agent-card--vlist {
  width: 100%;
  height: 100%;
  max-height: 60px;
  min-height: 0;
  margin-bottom: 0;
  padding: 6px 10px;
  gap: 8px;
}

.qr-transfer-scroller .qr-agent-avatar-wrap--vlist {
  width: 36px;
  height: 36px;

  ::v-deep .head-image {
    width: 36px;
    height: 36px;
  }
}

.qr-agent-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
  height: 60px;
  padding: 10px 13px;
  border-radius: 16px;
  border: 1px solid rgba(195, 198, 215, 0.6);

  &:last-of-type {
    margin-bottom: 0;
  }

  &.is-selected {
    border-color: #2563eb;
    background: #eff6ff;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.12);
  }

  &:hover {
    border-color: #2563eb;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
}

.qr-transfer-actions {
  margin-top: 12px;
}

.qr-transfer-confirm-btn {
  width: 100%;
}

.qr-agent-avatar-wrap {
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 44px;

  ::v-deep .head-image {
    display: block;
    width: 44px;
    height: 44px;
  }
}

.qr-agent-name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 22px;
  color: #303133;
  text-align: left;
}

.qr-capacity-tag {
  flex-shrink: 0;
  display: flex;
  min-width: 25px;
  width: auto;
  height: auto;
  padding: 3px 6px;
  justify-content: center;
  align-items: center;
  font-size: 8px;
  font-weight: 400;
  line-height: 20px;
  color: #434654;
  border-radius: 8px;
  background: #e7e7f3;

  &.is-full {
    color: #434654;
    background: #e15555;
  }
}

::v-deep .qr-remark-input.el-textarea .el-textarea__inner {
  padding: 10px 12px;
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.6;
  color: #303133;

  &::placeholder {
    color: #c0c4cc;
  }
}
</style>
